import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getAccountProfile } from '#/api/account';
import { useApi } from '#/api/fetch-api';

import { useWalletView } from '#/features/wallet/wallet-context';

import createSafeContext from '#/utils/createSafeContext';

import { AccountProfileView, createView } from './account-profile-views';
import { AccountProfileState, INITIAL_STATE } from './account-profile-state';
import {
  AccountProfileActions,
  createActions,
} from './account-profile-actions';
import { useUpdate } from './account-profile-context-service';

export const [useAccountProfileState, AccountProfileStateProvider] =
  createSafeContext<AccountProfileState>('AccountProfileState');

export const [useAccountProfileView, AccountProfileViewProvider] =
  createSafeContext<AccountProfileView>('AccountProfileView');

export const [useAccountProfileActions, AccountProfileActionsProvider] =
  createSafeContext<AccountProfileActions>('AccountProfileActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function AccountProfileProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const view = useMemo(() => createView(state), [state]);

  const update = useUpdate();
  const service = useMemo(() => ({ update }), [update]);

  const actions = useMemo(
    () => createActions(setState, service),
    [setState, service],
  );

  useAccountProfileFetch(setState);

  return (
    <AccountProfileActionsProvider value={actions}>
      <AccountProfileStateProvider value={state}>
        <AccountProfileViewProvider value={view}>
          {props.children}
        </AccountProfileViewProvider>
      </AccountProfileStateProvider>
    </AccountProfileActionsProvider>
  );
}

function useAccountProfileFetch(
  setState: Dispatch<SetStateAction<AccountProfileState>>,
): void {
  const walletView = useWalletView();
  const isReady = walletView.isSignedIn;

  const [_, exec] = useApi(getAccountProfile);

  useEffect(() => {
    if (!isReady) return;

    exec({}, (resp) => {
      setState((state) => ({
        ...state,
        status: 'loading',
      }));

      if (!resp.ok) {
        setState((state) => ({
          ...state,
          status: 'error',
          error: `Failed to load account profile: ${resp.error.message}.`,
        }));
        return;
      }

      setState((state) => ({
        ...state,
        status: 'connected',
        profile: resp.data,
      }));
    });
  }, [exec, isReady, setState]);
}
